// src/components/NotificationDropdown.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Notifications, NotificationIcons } from '../data/notifications';
import { BellIcon, CheckIcon, XIcon } from './Icons';

function NotificationDropdown({ user }) {
  const [isOpen, setIsOpen]             = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]   = useState(0);
  const dropdownRef = useRef(null);

  const isCustomer = user?.role === 'customer';

  const refreshNotifications = () => {
    const visible = Notifications.getForUser(user?.id, isCustomer, user?.role);
    setNotifications(visible.slice(0, 15));
    setUnreadCount(visible.filter(n => !n.read).length);
  };

  useEffect(() => {
    refreshNotifications();
    const handleUpdate = () => refreshNotifications();
    window.addEventListener('notificationAdded', handleUpdate);
    window.addEventListener('notificationsUpdated', handleUpdate);
    return () => {
      window.removeEventListener('notificationAdded', handleUpdate);
      window.removeEventListener('notificationsUpdated', handleUpdate);
    };
  }, [user?.id, isCustomer]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAllRead = () => {
    Notifications.markAllReadForUser(user?.id, isCustomer);
  };

  const handleMarkAsRead = (id) => {
    Notifications.markAsRead(id);
  };

  const handleDelete = (id, e) => {
    e.stopPropagation();
    Notifications.delete(id);
  };

  const handleClearAll = () => {
    Notifications.clearForUser(user?.id, isCustomer);
  };

  return (
    <div className="notif-wrapper" ref={dropdownRef}>
      <button
        className="topbar-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notif-dropdown">
          <div className="notif-header">
            <span className="notif-title">Notifications</span>
            {unreadCount > 0 && (
              <button className="notif-mark-all" onClick={handleMarkAllRead}>
                <CheckIcon /> Mark all read
              </button>
            )}
          </div>

          <div className="notif-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">
                <BellIcon />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map(notif => (
                <div
                  key={notif.id}
                  className={`notif-item ${!notif.read ? 'unread' : ''}`}
                  onClick={() => handleMarkAsRead(notif.id)}
                >
                  <div className="notif-icon">
                    {NotificationIcons[notif.type] || '📌'}
                  </div>
                  <div className="notif-content">
                    <div className="notif-item-title">{notif.title}</div>
                    <div className="notif-message">{notif.message}</div>
                    <div className="notif-time">{Notifications.timeAgo(notif.createdAt)}</div>
                  </div>
                  {notif.whatsapp_link && (
                    <a href={notif.whatsapp_link} target="_blank" rel="noopener noreferrer"
                      className="notif-wa-btn" onClick={e => e.stopPropagation()}
                      title="Send via WhatsApp" style={{ fontSize: 14, textDecoration: 'none', marginRight: 4 }}>
                      📱
                    </a>
                  )}
                  <button
                    className="notif-delete"
                    onClick={(e) => handleDelete(notif.id, e)}
                  >
                    <XIcon />
                  </button>
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notif-footer">
              <button onClick={handleClearAll}>Clear all</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationDropdown;
